/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { EvmTokenMetadata, ExternalTokenData, FilterState, IbcTokenMetadata, TokenData } from '../../constants/DataModels'
import ServerTable, { Column, FilterConfig } from '../Tables/ServerTable'

import { formatAge, formatBridgeId, formatChain, formatUsd, maskHash } from 'src/constants/ConversionUtils'
import { StatsState } from '../../store/stats/StatsTypes'
import { useAppSelector } from '../../store/store'
import './ExternalTokensPage.css'
import { BridgeId, getBridgeNameFromBridgeId } from 'src/constants/BlockchainConstants'
import { fetchExternalTokensData, formatParamsForApi } from 'src/api/api'
import { RequestDataParams } from 'src/components/Tables/ServerTable'
import { BlockchainWithIcon } from 'src/components/shared/BlockchainWithIcon'
import { CopyText } from 'src/components/shared/CopyText'
import { TokenInfo } from 'src/components/shared/TokenInfo'
import { ExplorerAssetLink } from 'src/components/shared/ExplorerAssetLink'
import dayjs from 'dayjs'
import { IBCPathDisplay } from 'src/components/shared/IBCPathDisplay'
import { useSearchParams } from 'react-router-dom'

export const EXTERNAL_TOKEN_COLUMNS: Column<ExternalTokenData>[] = [
  {
    id: 'bridge_id',
    header: 'Bridge',
    canSort: true,
    cellRenderer: (row) => {
      return getBridgeNameFromBridgeId(row.bridge_id)
    },
  },
  {
    id: 'blockchain',
    header: 'Blockchain',
    cellRenderer: (row) => {
      const { blockchain } = row
      return BlockchainWithIcon({
        blockchain: blockchain,
      })
    },
  },
  {
    id: 'connection_id',
    header: 'Connection ID',
    cellRenderer: (row) => {
      const { connection_id } = row
      return <CopyText
        className={'connection-id-label'}
        text={maskHash(connection_id, 20)}
        copyText={connection_id}
      />
    },
  },
  {
    id: 'is_carbon_owned',
    canSort: true,
    header: 'Source',
    cellRenderer: (row) => {
      return <>{row.is_carbon_owned ? 'Deployed' : 'External'}</>
    },
  },
  // {
  //   id: 'is_enabled',
  //   header: (prop: any) => {
  //     return (
  //       <Sortableheader
  //         handleSort={prop.onSort}
  //         headerName="Enabled"
  //         headerId="is_enabled"
  //       />
  //     )
  //   },
  //   accessor: 'is_enabled',
  //   cellRenderer: (props: cellRendererProps<ExternalTokenData, string>) => {
  //     return <>{props.value ? '✅' : '❌'}</>
  //   },
  // },
  {
    id: 'demex_token',
    header: 'Demex Token',
    cellRenderer: (row) => {
      return (
        <TokenInfo token={row.token} />
      )
    },
  },
  {
    id: 'external_address',
    header: 'External Token',
    cellRenderer: (row) => {
      const { blockchain, external_address } = row
      return (
        <div>
          {renderMetaData(row)}
          <ExplorerAssetLink blockchain={blockchain}
                             assetDenomOrAddress={external_address}
                             shouldTruncate={true}
          />
        </div>
      )
    },
  },
  {
    id: 'token_price',
    header: 'Latest Price',
    cellRenderer: (row) => {
      const { token } = row
      const { price_usd, price_usd_updated_at } = token

      const isStale = dayjs().diff(dayjs(price_usd_updated_at), 'minute') > 10
      const updatedLabel = isStale ? formatAge(price_usd_updated_at) : 'Updated Recently'

      return (
        <div>
          {formatUsd(price_usd)} <br />
          <small>{updatedLabel}</small>
        </div>
      )
    },
  },
]

const renderMetaData = (externalToken: ExternalTokenData) => {
  const { bridge_id, metadata } = externalToken
  switch (bridge_id) {
    case BridgeId.IBC: {
      const { base_denom, path } = metadata as IbcTokenMetadata
      return <IBCPathDisplay path={path} baseDenom={base_denom}
                             counterPartyChain={externalToken.blockchain}
      />
    }
    case BridgeId.Axelar: {
      const { name, symbol, decimals } = metadata as EvmTokenMetadata
      return (
        <div className={'erc20-token-details'}>
          <div>{symbol} <span className="decimals-pill">{decimals} d.p.</span><br /></div>
          <small>{name}</small>
        </div>
      )
    }
  }
  return (
    <div>Deprecated</div>
  )
}

const ExternalTokensPage: React.FC = () => {
  const selector = useAppSelector

  // state from supported_blockchains
  const statsState: StatsState = selector((state) => state.StatsReducer)
  const supportedBlockchains = statsState.supportedBlockchains.data
  const [searchParams] = useSearchParams()

  // request function
  const handleDataRequest = useCallback(async (params: RequestDataParams) => {
    const response = await fetchExternalTokensData(formatParamsForApi(params))
    return response.data
  }, [])

  // derive filter options
  const bridgeOptions = useMemo(() => {
    return Array.from(new Set(supportedBlockchains.map((row) => row.bridge_id)))
  }, [supportedBlockchains])
  const filterState: FilterState = Object.fromEntries(searchParams.entries())
  const selectedBridgeId = filterState.bridge_id
  const blockchainOptions = useMemo(() => {
    return Array.from(new Set(
      supportedBlockchains
        .filter((row) => row.blockchain !== 'polynetwork')
        .filter((row) => !selectedBridgeId || row.bridge_id.toString() === selectedBridgeId)
        .map((row) => row.blockchain)
    )).sort()
  }, [supportedBlockchains, selectedBridgeId])

  // filter options
  const filters: FilterConfig[] = useMemo(() => ([
    {
      name: 'Bridge',
      id: 'bridge_id',
      filterType: 'select',
      options: bridgeOptions.map((opt) => opt.toString()),
      optionFormatter: (val: string) => formatBridgeId(Number(val) as BridgeId),
    },
    {
      name: 'Blockchain',
      id: 'blockchain',
      filterType: 'select',
      options: blockchainOptions,
      optionFormatter: formatChain,
    },
    {
      name: 'Source',
      id: 'is_carbon_owned',
      filterType: 'select',
      options: ['true', 'false'],
      optionFormatter: (val: string) => val === 'true' ? 'Deployed' : 'External',
    },
    {
      name: 'Search denom',
      id: 'denom',
      filterType: 'text',
    },
  ]), [bridgeOptions, blockchainOptions])

  return (
    <div className="container main-wrapper external-tokens">
      <ServerTable name="Bridged Tokens"
                   columns={EXTERNAL_TOKEN_COLUMNS}
                   onRequestData={handleDataRequest}
                   filters={filters}
      />
    </div>
  )
}

export default ExternalTokensPage
